import {
  findBestMatchingEmployer,
  type EmployerMatchCriteria,
  type EmployerRecord,
} from '@/utils/people-search.utils';

const companyUrl = (value: unknown) =>
  typeof value === 'string'
    ? value
        .trim()
        .toLowerCase()
        .replace(
          /^https?:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\//,
          'linkedin.com/'
        )
        .replace(/[?#].*$/, '')
        .replace(/\/$/, '')
    : '';

/** Undefined preserves the ordinary People search path. An explicit empty list does not fall back to an unrelated employer. */
export function programEmployerCandidates(
  raw: Record<string, unknown> | undefined
): EmployerRecord[] | undefined {
  if (
    !raw ||
    (!Object.prototype.hasOwnProperty.call(
      raw,
      'startup_program_matched_employer_urls'
    ) &&
      !Object.prototype.hasOwnProperty.call(
        raw,
        'startup_program_selected_employer_url'
      ))
  )
    return undefined;
  const urls = Array.isArray(raw.startup_program_matched_employer_urls)
    ? raw.startup_program_matched_employer_urls
    : [];
  const allowed = new Set(urls.map(companyUrl).filter(Boolean));
  const employers = Array.isArray(raw.current_employers)
    ? raw.current_employers.filter(
        (employer): employer is EmployerRecord =>
          employer !== null &&
          typeof employer === 'object' &&
          !Array.isArray(employer)
      )
    : [];
  const candidates = employers.filter((e) =>
    allowed.has(companyUrl(e.company_linkedin_profile_url))
  );
  // The server evaluates the full employer predicate and uses this exact row
  // for its cumulative cap. Do not override it with frontend title scoring.
  if (
    Object.prototype.hasOwnProperty.call(
      raw,
      'startup_program_selected_employer_url'
    )
  ) {
    const chosenUrl = companyUrl(raw.startup_program_selected_employer_url);
    const chosen = candidates.find(
      (employer) =>
        chosenUrl &&
        companyUrl(employer.company_linkedin_profile_url) === chosenUrl
    );
    return chosen ? [chosen] : [];
  }
  return candidates;
}

export function programEmployer(
  raw: Record<string, unknown> | undefined,
  criteria: EmployerMatchCriteria
): EmployerRecord | undefined {
  const candidates = programEmployerCandidates(raw);
  return candidates?.length
    ? findBestMatchingEmployer(candidates, criteria)
    : undefined;
}

export function programPersonImportRaw(
  raw: Record<string, unknown>,
  criteria: EmployerMatchCriteria
): Record<string, unknown> {
  const candidates = programEmployerCandidates(raw);
  if (candidates === undefined) return raw;
  if (!candidates.length)
    throw new Error(
      'The program-matching employer is missing. Run the search again before importing.'
    );
  const best = findBestMatchingEmployer(candidates, criteria);
  const employers = raw.current_employers as EmployerRecord[];
  return {
    ...raw,
    // Fall back to the person's existing title so an employer with no title
    // never blanks out raw.title.
    title: best.employee_title || best.title || raw.title,
    current_employers: [best, ...employers.filter((e) => e !== best)],
  };
}
